import React, { useState } from 'react';
import { Box, Chip, IconButton, InputBase, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { formatCurrency, formatPercent, getDeptColor, getDeptShortName, TAR_STATUS, TAR_STATUS_META } from '../utils/deptDisplay';

// Same look as the Refund Overview cards (Dashboard/refund/TopCardOverview/CardModule.jsx):
// blue gradient, 16px radius, translucent circle in the top-right corner, white text.
export const statCardSx = {
  position: 'relative',
  overflow: 'hidden',
  minWidth: 150,
  height: '100%',
  borderRadius: '16px',
  color: '#fff',
  background: 'linear-gradient(135deg, #4f94d7 0%, #002F6C 100%)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
};
export const statCardCircleSx = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: 70,
  height: 70,
  background: 'rgba(255,255,255,0.1)',
  borderRadius: '50%',
  transform: 'translate(30%, -30%)',
};

export const CARD_MIN_HEIGHT = 124;

// One headline card - a department's, or the month's Total (give it a `title` and no `deptId`). Every card has the SAME
// shape so a row of them reads at a glance and lines up:
//
//     ● OGD                                      name (short, full name on hover)
//     [Top pace]                                 the best/worst-pace badge - its row exists in EVERY card of the grid
//                                                whenever any card has a badge (`badgeRow`), so the rows below line up
//     ₹ 1,97,32,599                              revenue so far
//     86.02%                      [On track]     achievement of the target + the pace status
//     ▓▓▓▓▓▓▓▓▓▓░░░░                             progress bar
//     Target ₹ 2,29,40,000                       the target
//
// With no target the last three rows become "No target set" and, when allowed, a small "Try a target" field.
//
// The status is judged against how much of the month has gone (see getPaceStatus), not against a flat cutoff; hover the
// chip for the numbers behind it. `highlight` marks the best/worst performer of the month.
//
// `isTemp`/`onSetTemp`/`onClearTemp`: with no real target someone can try one just to see the cards react - a view-only
// preview (localStorage, see deptDisplay.js), never saved to the backend, never offered when a real target exists.
// `allowTempTarget` is off for the "Unidentified" pseudo-department (deptId 0) and for the Total card.
export default function DepartmentStatCard({
  deptId,
  deptName,
  title,
  revenueToDate,
  target,
  tar,
  status,
  highlight,
  loading,
  isTemp,
  onSetTemp,
  onClearTemp,
  allowTempTarget = true,
  expectedToDate = 0,
  elapsedDays,
  daysInMonth,
  note,
  badgeRow = false,
}) {
  const [draft, setDraft] = useState('');

  if (loading) {
    return <Skeleton variant="rectangular" height={CARD_MIN_HEIGHT} sx={{ borderRadius: '16px' }} />;
  }

  const hasTarget = target > 0;
  const fullName = deptName || title || '';
  const shownTitle = title || getDeptShortName(deptId, deptName);
  // a target but no pace yet: the month has not started, which is not the same as "no target"
  const notStarted = hasTarget && status === TAR_STATUS.NO_TARGET;
  const meta = TAR_STATUS_META[status] || TAR_STATUS_META[TAR_STATUS.NO_TARGET];
  const statusLabel = notStarted ? 'Not started' : meta.label;
  const statusColor = notStarted ? 'default' : meta.color;
  const progressValue = hasTarget ? Math.min((revenueToDate / target) * 100, 100) : 0;
  const paceTip = notStarted
    ? 'The month has not started yet, so there is no pace to judge'
    : `${formatCurrency(revenueToDate)} collected against ${formatCurrency(Math.round(expectedToDate))} expected by day ${elapsedDays} of ${daysInMonth}`;

  const submitDraft = () => {
    const value = Number(draft);
    if (draft === '' || Number.isNaN(value) || value <= 0) return;
    onSetTemp?.(value);
    setDraft('');
  };

  return (
    <Box
      role="group"
      aria-label={fullName}
      sx={{ ...statCardSx, minWidth: 0, minHeight: CARD_MIN_HEIGHT, outline: isTemp ? '1px dashed rgba(255,255,255,0.7)' : 'none', outlineOffset: -4 }}
    >
      <Box sx={statCardCircleSx} />
      <Box sx={{ position: 'relative', zIndex: 1, p: 1.25, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, minHeight: 22 }}>
          {deptId !== undefined && deptId !== null && (
            // the department's colour - the same one its table column, chart slice and legend use
            <Box sx={{ width: 10, height: 10, flexShrink: 0, borderRadius: '50%', backgroundColor: getDeptColor(deptId), border: '2px solid rgba(255,255,255,0.9)' }} />
          )}
          <Tooltip title={fullName} arrow>
            <Typography variant="subtitle2" noWrap sx={{ minWidth: 0, fontWeight: 600, lineHeight: 1.4 }}>
              {shownTitle}
            </Typography>
          </Tooltip>
        </Box>
        {(badgeRow || highlight) && (
          <Box sx={{ display: 'flex', alignItems: 'center', height: 18 }}>
            {highlight && (
              <Chip
                size="small"
                label={highlight}
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#fff',
                  backgroundColor: highlight === 'Top pace' ? 'rgba(34,197,94,0.85)' : 'rgba(239,68,68,0.85)',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
        )}

        <Tooltip title={formatCurrency(revenueToDate)} arrow>
          <Typography noWrap sx={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '0.2px' }}>
            {formatCurrency(revenueToDate)}
          </Typography>
        </Tooltip>

        <Box sx={{ mt: 0.75, minHeight: 56, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {hasTarget ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
                  {formatPercent(tar)}
                </Typography>
                <Tooltip title={paceTip} arrow>
                  <Chip size="small" label={statusLabel} color={statusColor} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                </Tooltip>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  height: 5,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  '& .MuiLinearProgress-bar': { backgroundColor: '#fff', borderRadius: 3 },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
                <Tooltip title={`Target ${formatCurrency(target)}`} arrow>
                  <Typography variant="caption" noWrap sx={{ opacity: 0.85, minWidth: 0, lineHeight: 1.2 }}>
                    {isTemp ? 'Try-out target' : 'Target'} {formatCurrency(target)}
                  </Typography>
                </Tooltip>
                {isTemp && (
                  <Tooltip title="Clear temporary target (view only, not saved)">
                    <IconButton size="small" aria-label="Clear temporary target" onClick={onClearTemp} sx={{ p: 0.25, color: '#fff' }}>
                      <CloseRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.2 }}>
                No target set
              </Typography>
              {allowTempTarget && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 26,
                    pl: 1.25,
                    pr: 0.25,
                    borderRadius: '13px',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:focus-within': { backgroundColor: 'rgba(255,255,255,0.22)', borderColor: '#fff' },
                  }}
                >
                  <InputBase
                    type="number"
                    placeholder="Try a target"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submitDraft()}
                    inputProps={{ 'aria-label': `Try a target for ${fullName}`, min: 0 }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: '#fff',
                      fontSize: '0.75rem',
                      '& input': {
                        p: 0,
                        MozAppearance: 'textfield',
                        '&::placeholder': { color: 'rgba(255,255,255,0.75)', opacity: 1 },
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      },
                    }}
                  />
                  <Tooltip title="Preview with this target (not saved)">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Preview with this target"
                        onClick={submitDraft}
                        disabled={!draft}
                        sx={{ p: 0.25, color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' } }}
                      >
                        <CheckRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </>
          )}
          {note && (
            <Typography variant="caption" sx={{ opacity: 0.75, fontStyle: 'italic', lineHeight: 1.2 }}>
              {note}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
